// types.ts
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */

import React from 'react';
import { Input, InputProps } from 'reactstrap';

import { DateRangeFilter } from './DateRangeFilter';
import { InputFilter, SearchInputFilter } from './InputFilter';
import SelectedFilterBadge from './SelectedFiltersBar/SelectedFilterBadge';

/**
 * Option type for dropdown, multi-select, and radio buttons.
 * Used for displaying a label and associating a value with the option.
 */
export interface Option {
  /**
   * Display label for the option.
   */
  label: string;

  /**
   * Value sent to the backend, can be a string, number, or boolean.
   */
  value: string;

  /**
   * Selection of this option will deselect other options
   */
  deselectOthers?: boolean;

  /**
   * Deselect specific options
   */
  deselectSpecific?: string[];
}

export interface BaseConfigParams {
  id: string;
  label: string;
  tooltip?: React.ReactNode;
  iconClass?: string;
  enabled?: boolean;
  showInFilterMenu?: boolean; // New field added
}

export interface BaseFilterConfigParams extends BaseConfigParams {
  defaultSelectedValues?: any;
  currentSelectedValues?: any;
  placeholder?: string;
  required?: boolean;
}

/**
 * Base class for all filters, providing common fields and methods.
 */
export class BaseFilter {
  id: string;
  label: string;
  defaultSelectedValues?: any;
  currentSelectedValues?: any;
  placeholder?: string;
  tooltip?: React.ReactNode;
  required?: boolean;
  iconClass?: string; // New field added
  enabled: boolean; // New field added
  showInFilterMenu?: boolean; // New field added

  constructor(baseConfig: BaseFilterConfigParams) {
    this.id = baseConfig.id;
    this.label = baseConfig.label;
    this.defaultSelectedValues = baseConfig.defaultSelectedValues;
    this.currentSelectedValues = baseConfig.currentSelectedValues;
    this.placeholder = baseConfig.placeholder || 'TODO: Placeholder';
    this.tooltip = baseConfig.tooltip || 'TODO: Add some help text on tooltip';
    this.required = baseConfig.required;
    this.iconClass = baseConfig.iconClass || 'fas fa-filter'; // New field added
    this.enabled = baseConfig.enabled ?? true; // New field added
    this.showInFilterMenu = baseConfig.showInFilterMenu ?? true; // Default value set to true
  }

  /**
   * Render the filter.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  render(onSelectedValueChange: () => void) {
    return (
      <>
        {this.label}: {this.defaultSelectedValues}
      </>
    );
  }

  renderFilterBadge(onSelectedValueChange: () => void): JSX.Element | null {
    const selectedValues = this.getSelectedValues();
    if (!selectedValues || selectedValues.length === 0) return null;

    return (
      <SelectedFilterBadge
        key={this.id}
        label={this.label}
        values={selectedValues}
        onRemove={() => {
          this.resetSelectionToDefault();
          onSelectedValueChange();
        }}
      />
    );
  }

  /**
   * Get the selected values of the filter.
   * Returns the default selected values if there is no current selection.
   */
  getSelectedValues(): any {
    // if (this.id === 'dateRange') {
    //   // console.log({
    //   //   msg: `${this.label}: getSelectedValues called`,
    //   //   currentSelectedValues: this.currentSelectedValues,
    //   //   defaultSelectedValues: this.defaultSelectedValues,
    //   //   isNull: this.currentSelectedValues === null,
    //   //   isUndefined: this.currentSelectedValues === undefined,
    //   //   isEmptyString: this.currentSelectedValues === '',
    //   //   isEmptyArray:
    //   //     Array.isArray(this.currentSelectedValues) &&
    //   //     this.currentSelectedValues.length === 0,
    //   // });
    // }
    if (
      this.currentSelectedValues === null ||
      this.currentSelectedValues === undefined ||
      this.currentSelectedValues === '' ||
      (Array.isArray(this.currentSelectedValues) &&
        this.currentSelectedValues.length === 0)
    ) {
      return this.defaultSelectedValues;
    }
    // if (this.id === 'dateRange') {
    //   console.log(`${this.label}:returning currentSelectedValues`);
    // }
    return this.currentSelectedValues;
  }

  /**
   * Check if the current selection is different from the default selection.
   */
  isSelectionDifferentFromDefault(): boolean {
    const arraysEqual = (arr1: any[], arr2: any[]): boolean => {
      if (arr1.length !== arr2.length) return false;
      for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false;
      }
      return true;
    };

    const selectedValues = this.getSelectedValues();
    const defaultValues = this.defaultSelectedValues;
    let isDifferent = false;

    if (Array.isArray(selectedValues) && Array.isArray(defaultValues)) {
      isDifferent = !arraysEqual(selectedValues, defaultValues);
    } else {
      isDifferent = selectedValues !== defaultValues;
    }
    if (isDifferent) {
      // console.log({
      //   msg: 'isSelectionDifferentFromDefault called',
      //   defaultSelectedValues: this.defaultSelectedValues,
      //   selectedValues,
      // });
    } else {
      // console.log({
      //   filter: this.label,
      //   msg: 'NOT DIFFERENT : isSelectionDifferentFromDefault called',
      //   defaultSelectedValues: this.defaultSelectedValues,
      //   currentSelectedValues: this.currentSelectedValues,
      //   selectedValues,
      // });
    }
    return isDifferent;
  }

  /**
   * Reset the current selection to the default selection.
   */
  resetSelectionToDefault(): void {
    this.currentSelectedValues = undefined;
  }
}

export class FilterGroupConfig {
  id: string;
  label: string;
  tooltip?: React.ReactNode;
  iconClass?: string;
  enabled: boolean;
  showInFilterMenu?: boolean; // New field added
  filters: BaseFilter[];

  constructor(params: BaseConfigParams & { filters: BaseFilter[] }) {
    this.id = params.id;
    this.label = params.label;
    this.tooltip = params.tooltip || 'TODO: Add some help text on tooltip';
    this.iconClass = params.iconClass || 'fas fa-filter';
    this.enabled = params.enabled ?? true;
    this.showInFilterMenu = params.showInFilterMenu ?? true; // Default value set to true

    this.filters = params.filters;
  }
}

/**
 * Configuration for a input filter.
 * Allows for a free text input with an optional maximum length.
 * default type is 'text' however can be overridden by InputProps
 */
/**
 * @class InputFilterConfig
 * @extends BaseFilter
 *
 * @description
 * This class extends the BaseFilter class to provide a configurable input filter component.
 * It allows for customization of the input field through various properties such as `maxLength`
 * and `inputProps`. The default input type is 'text', but it can be overridden by providing
 * `inputProps` with a different type.
 *
 * @example
 * // Example usage:
 * const inputFilter = new InputFilterConfig({
 *   id: 'exampleInput',
 *   placeholder: 'Enter text',
 *   maxLength: 50,
 *   inputProps: { type: 'email' }
 * });
 *
 * // Render the input filter
 * inputFilter.render(() => {
 *   console.log('Input changed:', inputFilter.getSelectedValues());
 * });
 *
 * @param {Object} params - Configuration parameters for the input filter.
 * @param {number} [params.maxLength] - Optional maximum length for the input field.
 * @param {InputProps} [params.inputProps] - Optional additional properties for the input component.
 *
 * @method render
 * @memberof InputFilterConfig
 * @param {Function} onSelectedValueChange - Callback function to be called when the input value changes.
 * @returns {JSX.Element} The rendered input component.
 *
 * @property {number} [maxLength] - The maximum length of the input field.
 * @property {InputProps} [inputProps] - Additional properties for the input component.
 */
export class InputFilterConfig extends BaseFilter {
  inputProps?: React.ComponentProps<typeof Input>;
  renderLabel?: boolean = false;
  maxLength?: number;
  enableDebounce?: boolean;
  debounceTime?: number;
  labelWidth?: string;

  constructor(
    params: BaseFilterConfigParams & {
      renderLabel?: boolean;
      maxLength?: number;
      inputProps?: InputProps;
      enableDebounce?: boolean;
      debounceTime?: number;
      labelWidth?: string;
    },
  ) {
    super({ ...params });
    this.renderLabel = params.renderLabel || false;
    this.maxLength = params.maxLength;
    this.inputProps = params.inputProps;
    this.enableDebounce = params.enableDebounce || false;
    this.debounceTime = params.debounceTime || 300;
    this.labelWidth = params.labelWidth;
  }

  render(onSelectedValueChange: () => void) {
    return (
      <InputFilter
        id={this.id}
        key={`input-${this.id}`}
        placeholder={this.placeholder}
        value={this.getSelectedValues()?.[0] || ''}
        label={this.renderLabel ? this.label : undefined}
        maxLength={this.maxLength}
        enableDebounce={this.enableDebounce}
        debounceTime={this.debounceTime}
        labelWidth={this.labelWidth}
        inputProps={this.inputProps}
        onSelectedValueChange={(value) => {
          if (value) {
            this.currentSelectedValues = [value];
          } else {
            this.resetSelectionToDefault();
          }
          onSelectedValueChange();
        }}
      />
    );
  }
}

export class SearchInputFilterConfig extends InputFilterConfig {
  onFilterClick?: () => void;
  render(onSelectedValueChange: () => void) {
    return (
      <SearchInputFilter
        id={this.id}
        key={`search-input-${this.id}`}
        placeholder={this.placeholder}
        value={this.getSelectedValues()?.[0] || ''}
        maxLength={this.maxLength}
        enableDebounce={this.enableDebounce}
        debounceTime={this.debounceTime}
        inputProps={this.inputProps}
        onClear={() => {
          this.resetSelectionToDefault();
          onSelectedValueChange();
        }}
        onSelectedValueChange={(value) => {
          if (value) {
            this.currentSelectedValues = [value];
          } else {
            this.resetSelectionToDefault();
          }
          onSelectedValueChange();
        }}
        onFilterClick={this.onFilterClick}
      />
    );
  }
}

export class DateRangeFilterConfig extends BaseFilter {
  labelWidth?: string; // New property for label width

  constructor(
    params: BaseFilterConfigParams & {
      labelWidth?: string;
    },
  ) {
    super({ ...params });
    this.labelWidth = params.labelWidth;
  }

  render(onSelectedValueChange: () => void) {
    return (
      <DateRangeFilter
        label={this.label}
        value={this.getSelectedValues()?.[0] || undefined}
        onSelectedValueChange={(value) => {
          // console.log('DateRangeFilter value changed:', {
          //   previousValue: this.currentSelectedValues,
          //   newValue: value,
          // });
          this.currentSelectedValues = value ? [value] : undefined;
          onSelectedValueChange();
        }}
        labelWidth={this.labelWidth} // Pass the labelWidth prop
      />
    );
  }

  private formatDateRangeToStringArray(value: {
    from: Date | null;
    to: Date | null;
  }): string[] {
    const { from, to } = value;
    const formattedValues: string[] = [];
    if (from) {
      formattedValues.push(
        `From: ${from.toLocaleDateString(undefined, {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}`,
      );
    }
    if (to) {
      formattedValues.push(
        `To: ${to.toLocaleDateString(undefined, {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}`,
      );
    }

    return formattedValues;
  }

  renderFilterBadge(onSelectedValueChange: () => void): JSX.Element | null {
    const selectedValues = this.getSelectedValues();
    if (!selectedValues || selectedValues.length === 0) return null;

    const formattedValues = this.formatDateRangeToStringArray(
      selectedValues[0],
    );

    return (
      <SelectedFilterBadge
        key={this.id}
        label={this.label}
        values={formattedValues}
        onRemove={() => {
          this.resetSelectionToDefault();
          onSelectedValueChange();
        }}
      />
    );
  }
}

export type Filters = BaseFilter | FilterGroupConfig;

/**
 * Unified type for all filter configurations.
 * This type encompasses all the possible filter configurations.
 */
export class FilterConfig {
  filters: Filters[] = [];
  searchFilterId: string = 'search';
  rightPanelFilters: string[] = [];
  leftPanelFilters: string[] = [];

  constructor({
    searchFilterId,
    rightPanelFilters,
    leftPanelFilters,
    filters,
  }: {
    searchFilterId: string;
    rightPanelFilters: string[];
    leftPanelFilters: string[];
    filters: Filters[];
  }) {
    this.searchFilterId = searchFilterId;
    this.rightPanelFilters = rightPanelFilters;
    this.leftPanelFilters = leftPanelFilters;
    this.filters = filters;
  }

  getActiveFilters(): BaseFilter[] {
    return this.filters.flatMap((filter) => {
      if (filter instanceof FilterGroupConfig) {
        // For FilterGroupConfig, check each sub-filter
        return filter.filters.filter((subFilter) =>
          subFilter.isSelectionDifferentFromDefault(),
        );
      } else if (filter instanceof BaseFilter) {
        // For BaseFilter, check if the selection is different from the default
        return filter.isSelectionDifferentFromDefault() ? [filter] : [];
      }
      return [];
    });
  }

  resetAllFilters = () => {
    this.getActiveFilters().forEach((filter) => {
      if (filter instanceof BaseFilter) {
        filter.resetSelectionToDefault();
      } else {
        console.error('Unexpected filter type:', filter);
      }
    });
  };

  getActiveFilterValues(): Record<string, any> {
    const activeFilterValues: Record<string, any> = {};

    this.getActiveFilters().forEach((filter) => {
      activeFilterValues[filter.id] = filter.getSelectedValues();
    });

    return activeFilterValues;
  }

  applyInitialFilterValues(initialFilterValues: Record<string, any>) {
    // // console.log('Applying initial filter values:', initialFilterValues);
    // Object.entries(initialFilterValues).forEach(([key, value]) => {
    //   console.log(`Initial filter value - Key: ${key}, Value: ${value}`);
    // });

    this.filters.forEach((filter) => {
      if (filter instanceof FilterGroupConfig) {
        filter.filters.forEach((subFilter) => {
          if (initialFilterValues[subFilter.id] !== undefined) {
            // console.log({
            //   msg: `Setting initial value for filter ${subFilter.label}`,
            //   value: initialFilterValues[subFilter.id],
            // });
            subFilter.currentSelectedValues = initialFilterValues[subFilter.id];
          }
        });
      } else if (filter instanceof BaseFilter) {
        if (initialFilterValues[filter.id] !== undefined) {
          // console.log({
          //   msg: `Setting initial value for filter ${filter.label}`,
          //   value: initialFilterValues[filter.id],
          // });
          filter.currentSelectedValues = initialFilterValues[filter.id];
        }
      }
    });
  }
}
